/*
  Condition tooltip (New / New other / Used), adapted from eBay's condition definitions.
  Any element with data-condition-tip="<condition>" is a trigger; the matching description
  is shown in a single shared panel appended to <body>, so it isn't clipped by
  overflow:hidden parents (e.g. the product card image).

  Mouse: hover to show. Touch: tap to toggle (a tap on a trigger inside a product card
  link doesn't navigate). Keyboard: focus to show, Escape to close.

  Used by snippets/condition-tooltip.liquid (product page) and assets/productCard.js
  (collection, search and shop-by-vehicle cards).
*/
(function () {
  if (window.conditionTip) return;

  const descriptions = {
    'new': 'A brand-new, unused, unopened, undamaged item in its original packaging (where packaging is applicable). Packaging should be the same as what is found in a retail store, unless the item was packaged by the manufacturer in non-retail packaging, such as an unprinted box or plastic bag. See the listing for full details.',
    'new other': 'A new, unused item with absolutely no signs of wear. The item may be missing the original packaging, or in the original packaging but not sealed. The item may be a factory second, or a new, unused item with defects. See the listing for full details and description of any imperfections.',
    'used': 'An item that has been used previously. The item may have some signs of cosmetic wear, but is fully operational and functions as intended. This item may be a floor model or store return that has been used. See the listing for full details and description of any imperfections.'
  };
  const labels = { 'new': 'New', 'new other': 'New other', 'used': 'Used' };
  const normalize = (condition) => String(condition || '').trim().toLowerCase();

  let panel = null;
  let activeTrigger = null;
  let hideTimer = null;
  let lastPointerType = 'mouse';

  function getPanel() {
    if (panel) return panel;
    panel = document.createElement('div');
    panel.className = 'condition-tip__panel';
    panel.id = 'condition-tip-panel';
    panel.setAttribute('role', 'tooltip');
    panel.addEventListener('mouseenter', () => clearTimeout(hideTimer));
    panel.addEventListener('mouseleave', () => scheduleHide());
    document.body.appendChild(panel);
    return panel;
  }

  function position(trigger) {
    const gutter = 16;
    const gap = 8;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;

    let left = Math.min(rect.left, viewportWidth - gutter - width);
    left = Math.max(gutter, left);

    let top = rect.bottom + gap;
    if (top + height > window.innerHeight - gutter && rect.top - gap - height > gutter) {
      top = rect.top - gap - height;
    }

    panel.style.left = left + 'px';
    panel.style.top = top + 'px';
  }

  function show(trigger) {
    const key = normalize(trigger.dataset.conditionTip);
    if (!descriptions[key]) return;
    clearTimeout(hideTimer);

    const el = getPanel();
    el.innerHTML = '';
    const title = document.createElement('strong');
    title.textContent = labels[key];
    const text = document.createElement('p');
    text.textContent = descriptions[key];
    el.append(title, text);
    el.classList.add('is-visible');

    if (activeTrigger && activeTrigger !== trigger) activeTrigger.setAttribute('aria-expanded', 'false');
    activeTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-describedby', el.id);
    position(trigger);
  }

  function hide() {
    clearTimeout(hideTimer);
    if (panel) panel.classList.remove('is-visible');
    if (activeTrigger) activeTrigger.setAttribute('aria-expanded', 'false');
    activeTrigger = null;
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, 120);
  }

  const triggerFrom = (target) => target && target.closest ? target.closest('[data-condition-tip]') : null;

  document.addEventListener('pointerdown', (e) => { lastPointerType = e.pointerType || 'mouse'; }, true);

  // Hover (mouse only)
  document.addEventListener('mouseover', (e) => {
    if (lastPointerType !== 'mouse') return;
    const trigger = triggerFrom(e.target);
    if (trigger) show(trigger);
  });
  document.addEventListener('mouseout', (e) => {
    const trigger = triggerFrom(e.target);
    if (trigger && !trigger.contains(e.relatedTarget)) scheduleHide();
  });

  // Tap (touch/pen) toggles; a click never follows the card link from the trigger on touch
  document.addEventListener('click', (e) => {
    const trigger = triggerFrom(e.target);
    if (!trigger) {
      if (panel && !panel.contains(e.target)) hide();
      return;
    }
    if (lastPointerType === 'mouse') return;
    e.preventDefault();
    if (activeTrigger === trigger) hide();
    else show(trigger);
  });

  // Keyboard
  document.addEventListener('focusin', (e) => {
    const trigger = triggerFrom(e.target);
    if (trigger && lastPointerType === 'mouse') show(trigger);
  });
  document.addEventListener('focusout', (e) => {
    if (triggerFrom(e.target) === activeTrigger && lastPointerType === 'mouse') scheduleHide();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
    if ((e.key === 'Enter' || e.key === ' ') && triggerFrom(e.target) === e.target) {
      e.preventDefault();
      activeTrigger === e.target ? hide() : show(e.target);
    }
  });

  window.addEventListener('scroll', () => { if (activeTrigger) hide(); }, { passive: true });
  window.addEventListener('resize', () => { if (activeTrigger) hide(); });

  window.conditionTip = { descriptions, isKnown: (condition) => !!descriptions[normalize(condition)] };
})();
